import { cn } from "@newxsystems/shared";
import type { AnchorHTMLAttributes } from "react";
import { getComponent } from "../../../../client";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { ITestimonialCardProps } from "./TestimonialCard.props";

const cardClasses =
  "block h-full min-w-0 rounded-[14px] border border-foreground bg-background p-6 text-start md:p-8";

export const TestimonialCard = ({ item }: ITestimonialCardProps) => {
  const Link = getComponent<AnchorHTMLAttributes<HTMLAnchorElement>>("Link");
  const cardContent = (
    <>
      <div className="flex min-w-0 items-start gap-5 md:gap-2 xl:gap-5">
        {item.portrait ? (
          <div className="h-20 w-20 shrink-0 overflow-hidden rounded-full border border-foreground/20 md:h-14 md:w-14 xl:h-24 xl:w-24">
            <BlockImage
              aspectRatioClassName="h-full w-full"
              className="h-full w-full"
              effectiveAspectRatio="1:1"
              image={item.portrait}
              imageClassName="object-cover"
              loading="lazy"
            />
          </div>
        ) : null}
        <div className="min-w-0 pt-1">
          <h3 className="text-2xl/[1.15] font-semibold tracking-[-0.02em] [overflow-wrap:anywhere] md:text-xl/[1.15] xl:text-2xl/[1.15]">
            {item.name}
          </h3>
          {item.attribution ? (
            <p className="mt-2 font-mono text-[12px]/[1.45] text-onSurface-500 [overflow-wrap:anywhere]">
              {item.attribution}
            </p>
          ) : null}
        </div>
      </div>
      <BlockRichText
        className="mt-8 text-base/[1.55] text-onSurface-500 [overflow-wrap:anywhere] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
        content={item.quote}
      />
    </>
  );

  if (item.cardLink) {
    if (!Link) {
      return null;
    }

    const opensNewTab = item.cardLink.openInNewTab === true;

    return (
      <Link
        aria-label={item.cardLink.accessibilityLabel ?? item.cardLink.label}
        className={cn(
          cardClasses,
          "border-electric-blue/40 transition-colors duration-200 hover:border-electric-blue focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-ring",
        )}
        href={item.cardLink.href}
        rel={opensNewTab ? "noopener noreferrer" : undefined}
        target={opensNewTab ? "_blank" : undefined}
      >
        {cardContent}
      </Link>
    );
  }

  return <article className={cardClasses}>{cardContent}</article>;
};

TestimonialCard.displayName = "TestimonialGrid::TestimonialCard";
